import { Component, Input, OnInit } from '@angular/core';
import dayjs from 'dayjs';
import { ClientConsultService } from 'src/app/services/client-consult.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { RedirectService } from 'src/app/services/redirect.service';
import { RouteReloadService } from 'src/app/services/reload-current-route';
import { ClientBankAccountOut, RegularWithdrawalDraft, RegularWithdrawalDraftStatus, RegularWithdrawalFrequencies } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';

@Component({
  selector: 'libero-rent-withdrawal-draft-card',
  templateUrl: './rent-withdrawal-draft-card.component.html',
  styleUrl: './rent-withdrawal-draft-card.component.scss',
  standalone: false,
})

export class RentWithdrawalDraftCardComponent implements OnInit {
  @Input({required: true}) public withdrawalDraft!: RegularWithdrawalDraft;
  @Input({required: true}) public investmentType!: 'portfolio' | 'strategy';
  @Input({required: true}) public aml!: boolean;

  public regularWithdrawalFrequencies = RegularWithdrawalFrequencies;

  public longTextBreakpoints = {
    value: 10000,
    accountNumber: 13,
  };

  public preloader = {
    accept: false,
    refuse: false,
  }

  public expireInDays: number | null = null;

  public get waitingForMailConfirm(): boolean {
    return this.withdrawalDraft.status === RegularWithdrawalDraftStatus.PENDING_DIP_APPROVAL;
  }

  public constructor(
    private modalService: ModalService,
    private toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private restApiService: RestApiService,
    private routeReloadService: RouteReloadService,
    private clientConsultService: ClientConsultService,
    private redirectService: RedirectService,
  ) {}

  public ngOnInit(): void {
    this.expireInDays = dayjs(this.withdrawalDraft.expiredAt).diff(dayjs(), 'day');
  }

  public refuseDraft(): void {
    this.preloader.refuse = true;
    this.restApiService.rejectWithdrawalDraft(this.withdrawalDraft.regularWithdrawalDraftId).subscribe({
      error:  (error) => this.acceptRefuseError(error),
      next: () => this.onRefuse(),
    });
  }

  public acceptDraft(): void {
    this.preloader.accept = true;
    if(this.withdrawalDraft.dip) {
      this.restApiService.getAvailableBankAccounts(this.withdrawalDraft.portfolioId)
        .subscribe({
          error: this.acceptRefuseError.bind(this),
          next: this.openDipAcceptModal.bind(this),
      });
    } else {
      this.restApiService.acceptWithdrawalDraft({
        portfolioId: this.withdrawalDraft.portfolioId,
        regularWithdrawalDraftId: this.withdrawalDraft.regularWithdrawalDraftId,
      }).subscribe({
        error:  (error) => this.acceptRefuseError(error),
        next: () => this.onAccept(),
      });
    }
  }

  private openDipAcceptModal(bankAccounts: ClientBankAccountOut[]): void {
      this.preloader.accept = false;
      this.modalService.openRegularWithdrawConfirmModal({
        formData: {
          portfolioId: this.withdrawalDraft.portfolioId,
          bankAccountId: this.withdrawalDraft.bankAccountId,
          mode: this.withdrawalDraft.mode,
          value: this.withdrawalDraft.value,
          frequency: this.withdrawalDraft.frequency,
          ...{ validTo: this.withdrawalDraft.validTo }
        },
        investmentType: this.investmentType,
        investmentName: this.withdrawalDraft.portfolioName,
        dip: this.withdrawalDraft.dip,
        bankAccountNumber: bankAccounts.find(
          bankAccount =>  bankAccount.clientBankAccountId === this.withdrawalDraft.bankAccountId)?.accountNumber
          ?? '',
        new: false,
        draftId: this.withdrawalDraft.regularWithdrawalDraftId,
      });
  }

  private onAccept(): void {
    this.preloader.accept = false;
    this.preloader.refuse = false;
    this.toastService.addNotification({
      message: 'toastNotifications.messages.withdrawalAccepted',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.routeReloadService.reloadCurrent();
  }

  private onRefuse(): void {
    this.preloader.accept = false;
    this.preloader.refuse = false;
    this.toastService.addNotification({
      message: 'toastNotifications.messages.withdrawalRefused',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.routeReloadService.reloadCurrent();
  }

  public goToAMLForm(): void {
    this.redirectService.goToAmlFormExternalForm();
  }

  private acceptRefuseError(error: unknown): void {
    this.preloader.accept = false;
    this.preloader.refuse = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public displayComment(): void {
    this.modalService.displayDraftWithdrawalComment({
      message: this.withdrawalDraft.message,
      colaborator: this.withdrawalDraft.collaborator,
      colaboratorPhotoUrl: this.clientConsultService.clientConsultImage.value,
    })
  }
}
